import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, CheckCircle2, AlertTriangle, Camera, Pen, Trash2 } from 'lucide-react';
import { registriesApi } from '../lib/api';
import { toast } from 'sonner';

const SignaturePad = ({ value, onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#023F80';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    if (value) {
      const img = new Image();
      img.onload = () => { ctx.clearRect(0,0,c.width,c.height); ctx.drawImage(img, 0, 0); };
      img.src = value;
    } else {
      ctx.clearRect(0,0,c.width,c.height);
    }
  }, [value]);

  const start = (e) => {
    drawing.current = true;
    const r = e.currentTarget.getBoundingClientRect();
    const ctx = ref.current.getContext('2d');
    ctx.beginPath();
    const x = (e.clientX ?? e.touches?.[0].clientX) - r.left;
    const y = (e.clientY ?? e.touches?.[0].clientY) - r.top;
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const ctx = ref.current.getContext('2d');
    const x = (e.clientX ?? e.touches?.[0].clientX) - r.left;
    const y = (e.clientY ?? e.touches?.[0].clientY) - r.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const url = ref.current.toDataURL('image/png');
    onChange?.(url);
  };
  const clear = () => {
    const c = ref.current;
    c.getContext('2d').clearRect(0,0,c.width,c.height);
    onChange?.('');
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={400}
        height={120}
        className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 cursor-crosshair touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button type="button" onClick={clear} className="mt-1 text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
        <Trash2 size={11} /> Pulisci firma
      </button>
    </div>
  );
};

const RegistryEntryDialog = ({ template, companyId, open, onOpenChange, onDone, existing = null }) => {
  const [data, setData] = useState({});
  const [operator, setOperator] = useState({ name: '', signature: '' });
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState(null); // {filename, dataUrl}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (existing) {
        setData(existing.data || {});
        setOperator(existing.operator || { name: '', signature: '' });
        setNote(existing.note || '');
      } else {
        setData({});
        setOperator({ name: '', signature: '' });
        setNote('');
      }
      setPhoto(null);
    }
  }, [open, existing]);

  if (!template) return null;

  const conformityHint = (() => {
    if (data.esito === 'non_conforme' || data.esito === 'respinto') return 'non_conforme';
    if (data.esito === 'conforme' || data.esito === 'accettato') return 'conforme';
    return null;
  })();

  const setField = (name, v) => setData((d) => ({ ...d, [name]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto({ filename: file.name, dataUrl: reader.result, size: file.size, type: file.type });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const required = (template.fields || []).filter((f) => f.required);
    for (const f of required) {
      const v = data[f.name];
      if (v === undefined || v === null || v === '') {
        toast.error(`Campo obbligatorio: ${f.label}`);
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        companyId,
        templateCode: template.code,
        data,
        operator,
        note,
        attachments: photo ? [photo] : [],
      };
      if (existing) {
        await registriesApi.updateEntry(existing.id, { data, operator, note });
      } else {
        await registriesApi.createEntry(body);
      }
      toast.success('Voce registrata');
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="registry-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#023F80] text-white">{template.macroArea}</span>
            {template.label}
          </DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(template.fields || []).map((f) => (
              <div key={f.name} className={`${(f.type==='textarea' || f.type==='signature' || f.type==='file') ? 'md:col-span-2' : ''}`}>
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  {f.label}{f.required && <span className="text-red-500">*</span>}
                  {f.unit && <span className="text-slate-400 font-normal">({f.unit})</span>}
                </label>
                {f.type === 'text' && <Input value={data[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'number' && <Input type="number" step="any" value={data[f.name] ?? ''} onChange={(e) => setField(f.name, e.target.value === '' ? '' : parseFloat(e.target.value))} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'date' && <Input type="date" value={data[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'time' && <Input type="time" value={data[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'datetime' && <Input type="datetime-local" value={data[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'textarea' && <Textarea rows={3} value={data[f.name] || ''} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" data-testid={`entry-field-${f.name}`} />}
                {f.type === 'boolean' && (
                  <div className="mt-2 flex items-center gap-2"><Checkbox checked={!!data[f.name]} onCheckedChange={(v) => setField(f.name, !!v)} data-testid={`entry-field-${f.name}`} /> <span className="text-xs">Sì</span></div>
                )}
                {f.type === 'select' && (
                  <Select value={data[f.name] || ''} onValueChange={(v) => setField(f.name, v)}>
                    <SelectTrigger className="mt-1" data-testid={`entry-field-${f.name}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {f.type === 'multiselect' && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(f.options || []).map((o) => {
                      const arr = Array.isArray(data[f.name]) ? data[f.name] : [];
                      const on = arr.includes(o);
                      return (
                        <button
                          key={o}
                          type="button"
                          onClick={() => setField(f.name, on ? arr.filter((x) => x !== o) : [...arr, o])}
                          className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${on ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                        >{o.replace(/_/g, ' ')}</button>
                      );
                    })}
                  </div>
                )}
                {f.type === 'file' && (
                  <Input type="file" onChange={onPhoto} className="mt-1" data-testid={`entry-field-${f.name}`} />
                )}
                {f.help && <p className="text-[10px] text-slate-500 mt-0.5">{f.help}</p>}
              </div>
            ))}

            <div className="md:col-span-2 border-t border-slate-100 pt-3 mt-2">
              <label className="text-xs font-semibold text-slate-700">Operatore</label>
              <Input value={operator.name} onChange={(e) => setOperator({ ...operator, name: e.target.value })} placeholder="Nome e cognome operatore" className="mt-1" data-testid="entry-operator" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Pen size={12} /> Firma digitale</label>
              <SignaturePad value={operator.signature} onChange={(s) => setOperator((o) => ({ ...o, signature: s }))} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Camera size={12} /> Foto / allegato</label>
              <Input type="file" accept="image/*,application/pdf" onChange={onPhoto} className="mt-1" data-testid="entry-photo" />
              {photo && <p className="text-[11px] text-slate-500 mt-1">Allegato: <strong>{photo.filename}</strong> ({Math.round(photo.size/1024)} KB)</p>}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-700">Note</label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="entry-note" />
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 flex-row items-center sm:justify-between">
          <div>
            {conformityHint && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${conformityHint === 'conforme' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                {conformityHint === 'conforme' ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />} {conformityHint.replace('_', ' ')}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold">Annulla</button>
            <button onClick={submit} disabled={busy} className="ro-hide px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#1e293b] disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1" data-testid="entry-submit">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} className="text-[#FFCB05]" />} {existing ? 'Aggiorna' : 'Registra'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegistryEntryDialog;
